'use client';

import { type FormEvent, type ReactNode, useId } from 'react';

import { Alert } from './Alert';
import { Button } from './Button';
import { IconDialog, type IconDialogTone } from './IconDialog';

export interface FormDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  tone?: IconDialogTone;
  /** Classe de largura do `IconDialog` (ex.: `w-[640px]`); limitada a 92vw. */
  width?: string;
  onClose: () => void;
  /** Ausente, o modal é só de leitura: some o botão de confirmar e fica só o de fechar. */
  onSubmit?: () => void;
  submitLabel?: string;
  closeLabel?: string;
  saving?: boolean;
  disabled?: boolean;
  /** Falha da última tentativa; aparece no topo do corpo com `role="alert"`. */
  error?: string | null;
  children?: ReactNode;
}

/**
 * Modal de formulário sobre o `IconDialog`: o corpo é um `<form>`, então Enter num campo
 * submete. O botão de confirmar fica no rodapé (fora do `<form>` no DOM) e se liga a ele pelo
 * atributo `form`. Enquanto salva, Esc, clique fora e "Cancelar" não fecham — fechar no meio
 * do envio esconderia o resultado da mutação. `noValidate` porque quem decide se pode enviar é
 * o consumidor, via `disabled`: a validação nativa do navegador mostraria balões fora do padrão.
 */
export function FormDialog({
  open,
  title,
  description,
  icon,
  tone,
  width = 'w-[640px]',
  onClose,
  onSubmit,
  submitLabel = 'Salvar',
  closeLabel = 'Cancelar',
  saving = false,
  disabled = false,
  error,
  children,
}: FormDialogProps) {
  const formId = useId();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !saving) onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // O submit sobe pela árvore React, portal incluído: aberto de dentro de outro formulário
    // (o diagnóstico do "Publicar agente"), ele dispararia também o formulário de fora.
    event.stopPropagation();
    if (!onSubmit || disabled || saving) return;
    onSubmit();
  };

  return (
    <IconDialog
      open={open}
      title={title}
      description={description}
      icon={icon}
      tone={tone}
      width={width}
      onOpenChange={handleOpenChange}
      footer={
        <>
          <Button variant="neutral-secondary" onClick={onClose} disabled={saving}>
            {closeLabel}
          </Button>
          {onSubmit && (
            <Button type="submit" form={formId} loading={saving} disabled={disabled || saving}>
              {submitLabel}
            </Button>
          )}
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 px-6 pb-5"
      >
        {error && (
          <Alert role="alert" variant="error" tone="filled" icon={null} description={error} />
        )}
        {children}
      </form>
    </IconDialog>
  );
}
